import React, { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { UploadCloud, Loader2, X, Film, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoUpload({ value, onChange, disabled = false, testid = "video-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file."); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error("Video must be under 100 MB."); return; }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      onChange(`${BACKEND_URL}${data.url}`);
      toast.success("Video uploaded — it will be reviewed before it appears publicly.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  if (value) {
    return (
      <div className="relative overflow-hidden rounded-xl border border-ff-border bg-black" data-testid={`${testid}-preview`}>
        <video src={value} controls className="max-h-72 w-full bg-black" />
        {!disabled && (
          <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange("")}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ff-red shadow-sm hover:bg-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      onClick={() => !disabled && !uploading && inputRef.current?.click()}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-ff-border bg-[#F3F4F6] p-8 text-center transition-colors duration-200 hover:border-ff-blue ${disabled ? "pointer-events-none opacity-60" : ""}`}
    >
      <input ref={inputRef} type="file" accept="video/*" className="hidden" data-testid={`${testid}-input`}
        onChange={(e) => upload(e.target.files?.[0])} disabled={disabled} />
      {uploading ? (
        <>
          <Loader2 className="mb-3 h-8 w-8 animate-spin text-ff-red" />
          <span className="text-sm font-semibold text-ff-blue">Uploading… {progress}%</span>
        </>
      ) : (
        <>
          <Film className="mb-3 h-8 w-8 text-ff-medium" />
          <span className="text-sm font-semibold text-ff-blue">Upload a campaign video</span>
          <span className="mt-1 text-xs text-ff-medium">MP4, MOV, WebM or M4V · up to 100 MB</span>
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-ff-blue">
            <ShieldCheck className="h-3.5 w-3.5 text-ff-green" /> Reviewed for safety before going live
          </span>
        </>
      )}
    </div>
  );
}
